import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import fixidLogo from "@/assets/fixid-logo.jpeg";
import { detectInviteMode } from "@/lib/passwordSetup";

// Captured at module load, before supabase-js clears the URL hash (FIX-260).
const INITIAL_INVITE =
  typeof window !== "undefined" && detectInviteMode(window.location.search, window.location.hash);

/**
 * Landing page for password recovery links and invitation links.
 * Invitees (mode=invite) must set a password before using the app.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const isInvite =
    INITIAL_INVITE || detectInviteMode(window.location.search, window.location.hash);
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) {
        setHasSession(true);
        setReady(true);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasSession(!!session);
      setReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const canSubmit = password.length >= 8 && password === confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password, data: { password_set: true } });
      if (error) throw error;
      // Refresh so the guard sees password_set=true immediately.
      await supabase.auth.refreshSession();
      toast.success(t("resetPassword.success"));
      navigate("/work-orders");
    } catch (err: any) {
      toast.error(err.message || t("resetPassword.failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <img src={fixidLogo} alt="FixID" className="mx-auto h-16 w-auto" />
          <div>
            <CardTitle className="text-2xl font-bold">
              {isInvite ? t("resetPassword.inviteTitle") : t("resetPassword.title")}
            </CardTitle>
            <CardDescription className="mt-2">
              {isInvite ? t("resetPassword.inviteSubtitle") : t("resetPassword.subtitle")}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {!ready ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : !hasSession ? (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {isInvite ? t("resetPassword.inviteInvalid") : t("resetPassword.invalidLink")}
                </AlertDescription>
              </Alert>
              <Button className="w-full" onClick={() => navigate("/login")}>
                {t("resetPassword.backToSignIn")}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="new-password">{t("resetPassword.newPassword")}</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    aria-label={show ? t("login.hidePassword") : t("login.showPassword")}
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {password && password.length < 8 && (
                  <p className="text-xs text-destructive mt-1">{t("resetPassword.minLength")}</p>
                )}
              </div>
              <div>
                <Label htmlFor="confirm-new-password">{t("resetPassword.confirmPassword")}</Label>
                <Input
                  id="confirm-new-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                />
                {confirm && password !== confirm && (
                  <p className="text-xs text-destructive mt-1">{t("resetPassword.mismatch")}</p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={!canSubmit || saving}>
                {saving ? t("resetPassword.saving") : t("resetPassword.submit")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
